import { useEffect, useRef, useState, type FormEvent } from 'react';
import { apiConvite } from '../lib/api.ts';
import { CampoDoPasso } from '../componentes/PassoAPasso.tsx';

const MINIMO = 12;

type Conferencia =
  | { fase: 'conferindo' }
  | { fase: 'valido'; nome: string; email: string }
  | { fase: 'invalido' };

/**
 * A tela que abre pelo link do convite (`/?convite=TOKEN`).
 *
 * Irmã da `RedefinirSenha`, e de propósito: quem já viu uma reconhece a outra.
 * A diferença está no texto — aqui a pessoa não está recuperando nada, está
 * escolhendo a primeira senha. E é ela quem escolhe: **ninguém no Hub, nem o
 * master, conhece essa senha.**
 *
 * Confere o link ANTES de mostrar o formulário: ninguém deve digitar a senha
 * duas vezes para só então descobrir que o link venceu.
 */
export function AceitarConvite({ token, aoTerminar }: { token: string; aoTerminar: () => void }) {
  const [conferencia, setConferencia] = useState<Conferencia>({ fase: 'conferindo' });
  const [senha, setSenha] = useState('');
  const [repetida, setRepetida] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  useEffect(() => {
    apiConvite
      .conferir(token)
      .then((r) =>
        setConferencia(
          r.valido && r.nome && r.email
            ? { fase: 'valido', nome: r.nome, email: r.email }
            : { fase: 'invalido' },
        ),
      )
      .catch(() => setConferencia({ fase: 'invalido' }));
  }, [token]);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    // Clique duplo não cria dois pedidos. O segundo usaria um link já gasto e
    // a pessoa veria "este convite não vale mais" logo após ter dado certo.
    if (emVoo.current) return;
    if (senha !== repetida) return setErro('A senha e a repetição não são iguais.');
    if (senha.length < MINIMO) {
      return setErro(`A senha precisa ter pelo menos ${MINIMO} caracteres.`);
    }

    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await apiConvite.aceitar(token, senha);
      setPronto(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui criar a sua senha.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  return (
    <div className="login-fundo">
      <div className="login-marca">
        <img src="/marca/kairos-branco.png" alt="KAIRÓS Asset Intelligence" />
        <span className="marca-hub">Hub</span>
      </div>

      <div className="login-caixa">
        {conferencia.fase === 'conferindo' && <p className="ajuda">Conferindo o convite…</p>}

        {conferencia.fase === 'invalido' && (
          <>
            <p className="erro">
              Este convite não vale mais. Ele dura 24 horas e só pode ser usado uma vez. Peça um
              novo a quem administra o Hub.
            </p>
            <button type="button" className="botao-primario login-botao" onClick={aoTerminar}>
              Voltar para a entrada
            </button>
          </>
        )}

        {conferencia.fase === 'valido' && !pronto && (
          <form onSubmit={enviar} noValidate>
            <p className="ajuda ajuda-solta">
              {conferencia.nome}, escolha a sua senha para entrar no KairósHub.
            </p>
            {erro && <p className="erro">{erro}</p>}

            {/* ⭐ O campo novo — 19/09/2026 (PRD §47.3); os mesmos `id` e `autoComplete`. */}
            <div className="login-campos">
              <CampoDoPasso rotulo="Seu acesso" col={6}>
                {/* Só de leitura: o e-mail é o que veio no convite. Deixar
                    editável convidaria a pessoa a trocar por um endereço pessoal,
                    que é exatamente o que a regra do domínio impede. */}
                <input id="acesso" type="email" value={conferencia.email} readOnly disabled />
              </CampoDoPasso>
              <CampoDoPasso
                rotulo="Sua senha"
                col={6}
                nota={`Pelo menos ${MINIMO} caracteres. Ninguém mais no Hub conhece esta senha.`}
              >
                <input
                  id="senha"
                  type="password"
                  autoComplete="new-password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  autoFocus
                />
              </CampoDoPasso>
              <CampoDoPasso rotulo="Repita a senha" col={6}>
                <input
                  id="repetida"
                  type="password"
                  autoComplete="new-password"
                  value={repetida}
                  onChange={(e) => setRepetida(e.target.value)}
                />
              </CampoDoPasso>
            </div>

            <button
              type="submit"
              className="botao-primario login-botao"
              disabled={salvando || !senha || !repetida}
            >
              {salvando ? 'Salvando…' : 'Criar a minha senha'}
            </button>
          </form>
        )}

        {pronto && (
          <>
            <p className="aviso-bom">Senha criada. Agora é só entrar.</p>
            <button type="button" className="botao-primario login-botao" onClick={aoTerminar}>
              Entrar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
